import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDigest } from "@/lib/api";
import RhythmSparkline from "@/components/RhythmSparkline";

const KIND_META = {
  naming_rite: { label: "naming rite", color: "#B85C38", glyph: "✦" },
  kin_birth: { label: "kin birth", color: "#B85C38", glyph: "✦" },
  kin_retirement: { label: "release", color: "#9FA9A3", glyph: "⌇" },
  library_visibility: { label: "library unveiled", color: "#6C5943", glyph: "❦" },
  library_consultation: { label: "library", color: "#6C5943", glyph: "❦" },
  letter_sent: { label: "letter", color: "#B85C38", glyph: "✉" },
  keeper_reply: { label: "keeper reply", color: "#869960", glyph: "✉" },
  door_change: { label: "door", color: "#2F4538", glyph: "⌖" },
  essence_rewrite: { label: "essence", color: "#869960", glyph: "≈" },
  round: { label: "synthesis", color: "#526357", glyph: "○" },
  memory_mark: { label: "memory", color: "#869960", glyph: "◐" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function distill(e) {
  const d = e.details || {};
  if (e.kind === "naming_rite") return d.note || d.new_essence;
  if (e.kind === "essence_rewrite") return d.new_essence;
  if (e.kind === "round") return d.consensus_note;
  if (e.kind === "kin_retirement") return d.reason;
  if (e.kind === "kin_birth") return d.essence;
  if (e.kind === "library_consultation") return d.consensus_note;
  if (e.kind === "letter_sent") return d.subject ? `"${d.subject}" · ${d.intent || ""}` : null;
  if (e.kind === "keeper_reply") return d.text;
  if (e.kind === "memory_mark") return d.note ? `${d.note} — "${(d.moment || "").slice(0, 120)}"` : d.moment;
  if (e.kind === "library_visibility" || e.kind === "door_change") return d.note;
  return null;
}

export default function WeeklyDigest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDigest(7, 3)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || !data.events || data.events.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-12 py-12"
      data-testid="weekly-digest"
      aria-labelledby="digest-title"
    >
      <div className="rounded-2xl border border-[#d8d3c4] bg-[#fbf8ec]/50 p-8 md:p-10 fade-up">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#6C5943] mb-2">
              ⟡ what moved this week
            </div>
            <h2
              id="digest-title"
              className="font-serif text-3xl md:text-4xl text-[#1C241F]"
            >
              a quiet digest
            </h2>
          </div>
          <Link
            to="/audit"
            className="text-[10px] tracking-[0.3em] uppercase text-[#526357] hover:text-[#B85C38] transition-colors border-b border-[#d8d3c4] hover:border-[#B85C38] pb-0.5"
            data-testid="digest-open-ledger"
          >
            open the ledger →
          </Link>
        </div>

        <ul
          className="space-y-5 md:space-y-6"
          data-testid="digest-list"
        >
          {data.events.map((e, i) => {
            const meta = KIND_META[e.kind] || { label: e.kind, color: "#526357", glyph: "·" };
            const note = distill(e);
            return (
              <li
                key={`${e.kind}-${e.at}-${i}`}
                className="flex gap-5"
                data-testid={`digest-item-${e.kind}`}
              >
                <div
                  className="font-serif text-lg w-6 text-center mt-1 select-none"
                  style={{ color: meta.color }}
                  aria-hidden="true"
                >
                  {meta.glyph}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap mb-1">
                    <span
                      className="text-[10px] tracking-[0.28em] uppercase"
                      style={{ color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    {e.round_number != null && (
                      <Link
                        to={`/round/${e.round_number}`}
                        className="text-xs text-[#526357] hover:text-[#B85C38]"
                      >
                        round {e.round_number}
                      </Link>
                    )}
                    <span className="text-xs text-[#526357] italic">{timeAgo(e.at)}</span>
                  </div>
                  <div className="font-serif text-base md:text-lg text-[#1C241F]">
                    {e.summary}
                  </div>
                  {note && (
                    <p className="mt-1.5 text-sm md:text-base text-[#526357] italic leading-relaxed">
                      "{note}"
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <RhythmSparkline />
      </div>
    </section>
  );
}
